import {
  Box,
  Button,
  MantineProvider,
  SegmentedControl,
  Title,
} from '@mantine/core';
import {Notifications} from '@mantine/notifications';
import Head from 'next/head';
import {useRouter} from 'next/router';
import type React from 'react';
import {useEffect, useState} from 'react';
import {MdAdd, MdArrowBack, MdControlPoint, MdHome} from 'react-icons/md';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {FOLLOW_COMPANIES, MYPAGE} from '../../../config/constants/routes';
import {isReleasedRedesignTop} from '../../../config/unReleasePage';
import {
  useDelectFollowCompany,
  usePostFollowCompany,
} from '../../../core/repositories/mypageRepository';
import type {UsersMeFollowedCompaniesResponseType} from '../../../core/repositories/usersMeRepository';
import {CommonLink} from '../../../shared/components/CommonLink';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {CompanyCard} from '../../company/components/Container/Presenter/CompanyCard';
import {Navigation} from '../../top/components/Container/Presenter/Navigation/Navigation';
import {NAV_VALUE} from '../../top/components/Container/Presenter/Navigation/constants';

type Props = {
  userFollowedCompanies?: UsersMeFollowedCompaniesResponseType;
};

type followedCompany =
  UsersMeFollowedCompaniesResponseType['companies'][number] & {
    isDeleted: boolean;
  };

export const FavsCompaniesPage: React.FC<Props> = ({userFollowedCompanies}) => {
  const router = useRouter();
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.pathname)}`;
  const [followedCompanies, setFollowedCompanies] = useState<followedCompany[]>(
    [],
  );
  useEffect(() => {
    if (!userFollowedCompanies) return;
    setFollowedCompanies(
      userFollowedCompanies.companies.map((item) => ({
        ...item,
        isDeleted: false,
      })),
    );
  }, [userFollowedCompanies]);

  const saveDmSettings = usePostFollowCompany();

  const deleteDmSettings = useDelectFollowCompany();

  const submitPut = (companyId: number, isDeleted: boolean) => {
    if (isDeleted) {
      saveDmSettings.mutate(companyId, {
        onSuccess(data) {
          if (data.ok) {
            const newDataCopy = followedCompanies.map((item) => {
              if (item.id === companyId) {
                return {
                  ...item,
                  isDeleted: false,
                  followerCount: data.value?.followCompanyCount,
                };
              }

              return item;
            });
            setFollowedCompanies(newDataCopy);
          }
        },
      });
      return;
    }

    deleteDmSettings.mutate(companyId, {
      onSuccess(data) {
        if (data.ok) {
          const newDataCopy = followedCompanies.map((item) => {
            if (item.id === companyId) {
              return {
                ...item,
                isDeleted: true,
                followerCount: data.value?.followCompanyCount,
              };
            }

            return item;
          });
          setFollowedCompanies(newDataCopy);
        }
      },
    });
  };

  const {isPC} = useMediaQuery();
  const linkClassName =
    'bg-white text-primary-1 text-base font-bold grow col-end-11 col-span-2 pt-[5px]';

  if (isReleasedRedesignTop()) {
    return (
      <>
        <div className="pc:w-[1200px] mt-16">
          <div className="px-16 pc:px-0 hidden pc:block">
            <Navigation navValue={NAV_VALUE.MY_PAGE} setNavValue={() => {}} />
          </div>

          <div className="pc:hidden block bg-white rounded-16 flex justify-between p-8">
            <CommonLink href={MYPAGE}>
              <MdArrowBack
                className="bg-background rounded-[20px] p-4 mx-8 my-4"
                size={24}
              />
            </CommonLink>
            <div className="mt-4  text-lg font-bold">フォロー中の企業</div>
            <CommonLink
              href={FOLLOW_COMPANIES}
              className="bg-background pt-8 px-8 rounded-16 text-sm"
            >
              フォロー管理
            </CommonLink>
          </div>

          <div className="mt-8 text-center p-16 bg-white shadow-md rounded-16">
            <SegmentedControl
              data={[
                {label: '企業', value: '/mypage/favs/companies'},
                {label: '募集', value: '/mypage/follow/recruitment'},
                {label: 'ES・体験記', value: '/mypage/follow/report'},
              ]}
              onChange={(e) => {
                if (e === '/mypage/favs/companies') {
                  location.href = FOLLOW_COMPANIES;
                }
                if (e === '/mypage/follow/recruitment') {
                  location.href = '/mypage/follow/recruitment';
                }
                if (e === '/mypage/follow/report') {
                  location.href = '/mypage/follow/report';
                }
              }}
            />
          </div>
          <div className="p-16">
            {userFollowedCompanies &&
            userFollowedCompanies.companies.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-16">
                {userFollowedCompanies.companies.map((company) => (
                  <CompanyCard
                    bgColorStyle="bg-white"
                    width={300}
                    key={company.id}
                    company={{
                      ...company,
                      isEngineerCompany: false,
                    }}
                  />
                ))}
              </div>
            ) : (
              <p className="text-center">フォローしている企業がありません。</p>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Head>
        <title key="title">フォロー企業リスト | 外資就活ドットコム</title>
        <link rel="canonical" href={currentUrl} key="canonical" />
        <meta name="robots" content="noindex" />
      </Head>

      <Box className="flex min-h-screen flex-col items-center bg-background-1">
        <MantineProvider>
          <Notifications position={'top-right'} />
          <Box className="w-full max-w-[664px]">
            <Box>
              {isPC ? (
                <Title
                  order={1}
                  className="border-t-4 border-primary-1 bg-white p-[11px] text-lg text-primary-1 pc:px-32"
                >
                  <div className="flex justify-between items-center">
                    フォロー企業リスト
                    <Button
                      variant="outline"
                      type="submit"
                      className="justify-between bg-success-1 border-success-1 pc:hover:bg-success-1/90 pc:hover:duration-300"
                    >
                      <CommonLink
                        href="/mypage/follow_companies"
                        className="text-white flex items-center gap-8"
                      >
                        <MdAdd className="text-xl" />
                        企業をフォロー
                      </CommonLink>
                    </Button>
                  </div>
                </Title>
              ) : (
                <>
                  <Title
                    order={1}
                    className="border-t-2 border-primary-1 bg-white p-[11px] text-lg pc:px-32 flex"
                  >
                    <MdHome className="text-primary-1 text-xl" />
                    <div className="flex pl-8 justify-between items-center text-base text-black-1">
                      マイページ
                    </div>
                  </Title>
                  <div className="grid grid-cols-3 h-36">
                    <div className="text-center text-black-2 bg-black-5 p-4">
                      <a href="/">新着</a>
                    </div>
                    <div className="text-center text-primary-1 p-4">
                      フォロー
                    </div>
                    <div className="text-center text-black-2 bg-black-5 p-4">
                      <CommonLink href="/clip/reports">クリップ</CommonLink>
                    </div>
                  </div>
                </>
              )}
            </Box>
            <Box className="mt-[10px] mb-[20px] bg-white px-8 py-32 pc:px-32">
              <Box className="overflow-hidden list-none">
                {followedCompanies.map((company, index) => (
                  <Box
                    className={`grid ${
                      isPC ? 'grid-cols-11' : 'grid-cols-8'
                    } gap-4 border-b border-line py-4 ${
                      index === followedCompanies.length - 1
                        ? 'border-none'
                        : ''
                    }`}
                    key={index}
                  >
                    <Box className="col-span-5 items-center">
                      <CommonLink
                        href={`/company/${company.id}`}
                        className="bg-white text-primary-1 text-base font-bold pt-[5px]"
                      >
                        {company.name}
                      </CommonLink>
                    </Box>
                    {isPC ? (
                      company.studyUrl ? (
                        <CommonLink
                          href={`/company/${company.id}/experiences`}
                          className={linkClassName}
                        >
                          ES・体験記
                        </CommonLink>
                      ) : (
                        <Box className={linkClassName}>--</Box>
                      )
                    ) : null}
                    <Box
                      className={`flex rounded cursor-pointer pt-[2px] ${
                        isPC ? 'col-end-12' : 'col-end-9'
                      } mt-1 px-[3px] my-[5px] h-20 line-height-[20px] ${
                        company.isDeleted
                          ? 'bg-success-1/30 text-success-1 border border-success-1'
                          : 'bg-success-1 text-white'
                      }`}
                    >
                      <MdControlPoint />
                      <Box
                        className="text-xs pl-[3px]"
                        onClick={() => {
                          submitPut(company.id, company.isDeleted);
                        }}
                      >
                        {company.followerCount}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </MantineProvider>
      </Box>
    </>
  );
};
